import { takeLatest, put, call, takeEvery, cancelled } from "redux-saga/effects";
import { toast } from "react-toastify";
import historyRouter from 'historyRouter';

//Redux Actions
import CONSTANTS from "../CONSTANTS";
import PracticeOrdersService from "services/PracticeOrdersService";
import { 
  actionGetReservedKits, 
  actionSetOrdersList, 
  actionSetReservedKits, 
  actionGetOrdersList,
  actionSetEditOrder,
  actionSetKitDeleteLoading,
  actionSetOrderStateMachine,
} from "../actions/practiceOrdersActions";
import { actionGetKitsCount } from "store/actions/kitsActions";

function* sagaGetOrder({ orderId }) {
  const cancelToken = PracticeOrdersService.getSource();

  try {
    const result = yield call(PracticeOrdersService.getOrder, orderId, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Get order failed');
    }
    yield put(actionSetEditOrder(result.data));
    yield put(actionGetReservedKits(result.data._id));
  } catch (error) {
    toast.error(error.message || 'Get order failed');
  } finally {
      if (yield cancelled()) {
        cancelToken.cancel();
      }
    }
}

function* sagaGetOrderList({ page, limit, search }) {
  const cancelToken = PracticeOrdersService.getSource();

  try {
    const result = yield call(PracticeOrdersService.getOrdersList, page, limit, search, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Get orders failed');
    }
    yield put(actionSetOrdersList(result.data));
  } catch (error) {
    toast.error(error.message || 'Get orders failed');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaSubmitOrder({ data }) {
  const cancelToken = PracticeOrdersService.getSource();

  try {
    const result = yield call(PracticeOrdersService.createOrder, data, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Error creating the order');
    }
    yield put(actionGetReservedKits(result.data._id));
    yield put(actionGetKitsCount());
    yield put(actionSetEditOrder(result.data));
    toast.success("Order created successfully");
  } catch (error) {
    toast.error(error.message || 'Error creating the order');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaSubmitEdition({ data }) {
  const cancelToken = PracticeOrdersService.getSource();

  try {
    const result = yield call(PracticeOrdersService.editOrder, data, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Error editing the order');
    }
    yield put(actionGetReservedKits(result.data._id));
    yield put(actionGetKitsCount());
    yield put(actionSetEditOrder(result.data));
    toast.success("Order edited successfully");
  } catch (error) {
    toast.error(error.message || 'Error editing the order');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaGetReservedKits({ orderId }) {
  const cancelToken = PracticeOrdersService.getSource();

  try {
    const result = yield call(PracticeOrdersService.getReservedKits, orderId, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Error creating the order');
    }
    yield put(actionSetReservedKits(result.data));
    yield put(actionGetKitsCount());
  } catch (error) {
    toast.success(error.message || 'Error creating the order');
  } finally {
    yield put(actionSetKitDeleteLoading(false));
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaDeleteKitFromOrder({ orderId, kitId, kitType }) {
  const cancelToken = PracticeOrdersService.getSource();

  try {
    yield put(actionSetKitDeleteLoading(true));
    const result = yield call(PracticeOrdersService.deleteKitFromOrder, orderId, kitId, kitType, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Error deleting the kit from the order');
    }
    yield put(actionGetReservedKits(orderId));
    toast.success("Kit removed successfully");
  } catch (error) {
    toast.error(error.message || 'Error creating the kit from the order');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaCancelOrder({ orderId }) {
  const cancelToken = PracticeOrdersService.getSource();

  try {
    const result = yield call(PracticeOrdersService.cancelOrder, orderId, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Error cancelling the order');
    }
    yield put(actionGetOrdersList(0, 10));
    toast.success("Order cancelled successfully");
  } catch (error) {
    toast.error(error.message || 'Error cancelling the order');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaAutoAssignKits({ orderId }) {
  const cancelToken = PracticeOrdersService.getSource();

  try {
    yield put(actionSetKitDeleteLoading(true));
    const result = yield call(PracticeOrdersService.autoAssignKits, orderId, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Error assigning kits for the order');
    }
    yield put(actionSetEditOrder(result.data));
    yield put(actionGetReservedKits(result.data._id));
  } catch (error) {
    toast.error(error.message || 'Error assigning kits for the order');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaGetOrderStateMachine({}) {
  const cancelToken = PracticeOrdersService.getSource();
  try {
    const result = yield call(PracticeOrdersService.getStateMachine, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Error getting the order state machine');
    }
    yield put(actionSetOrderStateMachine(result.data));
  } catch (error) {
    toast.error(error.message || 'Error getting the order state machine');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

export function* practiceOrdersSaga() {
  console.log("*Main Orders saga");
  yield takeLatest(CONSTANTS.PRACTICE_ORDER_GET, sagaGetOrder);
  yield takeLatest(CONSTANTS.PRACTICE_ORDER_GET_LIST, sagaGetOrderList);
  yield takeLatest(CONSTANTS.PRACTICE_ORDER_SUBMIT_FORM, sagaSubmitOrder);
  yield takeLatest(CONSTANTS.PRACTICE_ORDER_GET_RESERVED_KITS, sagaGetReservedKits);
  yield takeLatest(CONSTANTS.PRACTICE_ORDER_DELETE_KIT, sagaDeleteKitFromOrder);
  yield takeLatest(CONSTANTS.PRACTICE_ORDER_CANCEL, sagaCancelOrder);
  yield takeLatest(CONSTANTS.PRACTICE_ORDER_AUTO_ASSIGN_KITS, sagaAutoAssignKits);
  yield takeLatest(CONSTANTS.PRACTICE_ORDER_SUBMIT_EDIT, sagaSubmitEdition);
  yield takeLatest(CONSTANTS.PRACTICE_ORDER_GET_STATE_MACHINE, sagaGetOrderStateMachine);
}
  